import { useId, useState, type ReactNode } from 'react'
import { NavLink, Route, Routes } from 'react-router-dom'
import SignInForm from '../components/event/SignInForm'
import TeamStage from '../components/admin/TeamStage'
import { useAdminEvent, type AdminEvent } from '../components/admin/useAdminEvent'
import { useSession } from '../lib/useSession'
import type { Person, ResultLink } from '../event/types'

const button =
  'rounded-md border border-[var(--page-rule)] px-3.5 py-2 text-sm font-semibold hover:bg-[var(--page-canvas-sunken)] disabled:opacity-50'
const primary =
  'rounded-md bg-[var(--color-accent-600)] px-3.5 py-2 text-sm font-bold text-white hover:bg-[var(--color-accent-700)] disabled:opacity-50'
const input = 'w-full rounded-md border border-[var(--page-rule)] bg-[var(--page-canvas)] px-2.5 py-1.5'

function Panel({ title, children, aside }: { title: string; children: ReactNode; aside?: ReactNode }) {
  const id = useId()
  return (
    <section aria-labelledby={id} className="rounded-lg border border-[var(--page-rule)] p-5">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h2 id={id} className="text-xl font-black tracking-tight">
          {title}
        </h2>
        {aside}
      </div>
      {children}
    </section>
  )
}

type SortKey = 'name' | 'email' | 'mpworks' | 'present' | 'onTeam'
type RosterSort = { key: SortKey; dir: 'asc' | 'desc' }

const SORT_COLUMNS: { key: SortKey; label: string }[] = [
  { key: 'name', label: 'Name' },
  { key: 'email', label: 'Email' },
  { key: 'mpworks', label: 'MPworks' },
  { key: 'present', label: 'Attendance' },
  { key: 'onTeam', label: 'On team' },
]

/** Sorts a copy of the roster; ties fall back to name so the order is stable. */
function sortRoster(roster: readonly Person[], { key, dir }: RosterSort, onTeam: ReadonlySet<string>): Person[] {
  const sign = dir === 'asc' ? 1 : -1
  const value = (p: Person) =>
    key === 'onTeam' ? (onTeam.has(p.id) ? 0 : 1) : key === 'mpworks' || key === 'present' ? (p[key] ? 0 : 1) : p[key].toLowerCase()
  return [...roster].sort((a, b) => {
    const x = value(a)
    const y = value(b)
    if (x < y) return -sign
    if (x > y) return sign
    return a.name.localeCompare(b.name)
  })
}

function Console({ admin }: { admin: AdminEvent }) {
  const { state, busy, act } = admin
  const [rosterText, setRosterText] = useState('')
  const [results, setResults] = useState<ResultLink[]>(state?.results ?? [])
  const [syncedRevision, setSyncedRevision] = useState(state?.revision)
  const [newPerson, setNewPerson] = useState({ name: '', email: '', mpworks: false })
  const [sort, setSort] = useState<RosterSort>({ key: 'name', dir: 'asc' })

  // Editable copies follow the saved document whenever it changes.
  if (state && syncedRevision !== state.revision) {
    setSyncedRevision(state.revision)
    setResults(state.results)
  }

  if (!state) return null

  const present = state.roster.filter((p) => p.present)
  const staff = present.filter((p) => p.mpworks).length
  // Checked as each person's team is revealed on the wheel.
  const onTeam = new Set(state.teams.slice(0, state.revealedCount).flatMap((t) => t.memberIds))

  return (
    <div className="flex flex-col gap-6">
      <Panel
        title="Roster"
        aside={
          <span className="text-sm text-[var(--page-ink-muted)]">
            {present.length} present of {state.roster.length}, {staff} MPworks
          </span>
        }
      >
        <form
          className="grid items-end gap-2 sm:grid-cols-[1fr_1fr_auto_auto]"
          onSubmit={async (e) => {
            e.preventDefault()
            const ok = await act({ type: 'addPerson', ...newPerson }, `${newPerson.name.trim()} added as present.`)
            if (ok) setNewPerson({ name: '', email: '', mpworks: false })
          }}
        >
          <label className="flex flex-col text-sm font-semibold">
            Name
            <input className={input} value={newPerson.name} onChange={(e) => setNewPerson({ ...newPerson, name: e.target.value })} required />
          </label>
          <label className="flex flex-col text-sm font-semibold">
            Email
            <input
              className={input}
              type="email"
              value={newPerson.email}
              onChange={(e) => setNewPerson({ ...newPerson, email: e.target.value })}
              required
            />
          </label>
          <label className="flex items-center gap-2 pb-2 text-sm font-semibold">
            <input type="checkbox" checked={newPerson.mpworks} onChange={(e) => setNewPerson({ ...newPerson, mpworks: e.target.checked })} />
            MPworks
          </label>
          <button type="submit" className={primary} disabled={busy || state.locked || !newPerson.name.trim() || !newPerson.email.trim()}>
            Add person
          </button>
        </form>

        {state.roster.length > 0 && (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full min-w-[36rem] text-left text-sm">
              <caption className="sr-only">Roster</caption>
              <thead>
                <tr className="border-b border-[var(--page-rule)]">
                  {SORT_COLUMNS.map((column) => (
                    <th
                      key={column.key}
                      scope="col"
                      className="py-2 pr-3"
                      aria-sort={sort.key === column.key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                    >
                      <button
                        type="button"
                        className="inline-flex cursor-pointer items-center gap-1 font-bold hover:underline"
                        onClick={() =>
                          setSort((current) =>
                            current.key === column.key
                              ? { key: column.key, dir: current.dir === 'asc' ? 'desc' : 'asc' }
                              : { key: column.key, dir: 'asc' },
                          )
                        }
                      >
                        {column.label}
                        <span aria-hidden="true" className="text-xs">
                          {sort.key === column.key ? (sort.dir === 'asc' ? '\u25B2' : '\u25BC') : '\u2195'}
                        </span>
                      </button>
                    </th>
                  ))}
                  <th scope="col" className="py-2"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {sortRoster(state.roster, sort, onTeam).map((p) => (
                  <tr key={p.id} className={`border-b border-[var(--page-rule)] ${p.present ? '' : 'text-[var(--page-ink-muted)]'}`}>
                    <td className="py-2 pr-3 font-semibold">{p.name}</td>
                    <td className="py-2 pr-3">{p.email}</td>
                    <td className="py-2 pr-3">
                      <input
                        type="checkbox"
                        className="size-4 cursor-pointer disabled:cursor-default"
                        aria-label={`${p.name} is MPworks staff`}
                        checked={p.mpworks}
                        disabled={busy || state.locked}
                        onChange={(e) => act({ type: 'updatePerson', id: p.id, patch: { mpworks: e.target.checked } })}
                      />
                    </td>
                    <td className="py-2 pr-3">
                      <button
                        type="button"
                        aria-pressed={p.present}
                        aria-label={`${p.name}: ${p.present ? 'present' : 'absent'}. Mark ${p.present ? 'absent' : 'present'}`}
                        disabled={busy || state.locked}
                        onClick={() => act({ type: 'updatePerson', id: p.id, patch: { present: !p.present } })}
                        className={`w-24 cursor-pointer rounded-md border px-2.5 py-1 text-sm font-bold hover:underline disabled:cursor-default disabled:opacity-50 ${
                          p.present
                            ? 'border-[var(--page-success)] text-[var(--page-success)]'
                            : 'border-[var(--page-rule)] text-[var(--page-ink-muted)]'
                        }`}
                      >
                        {p.present ? 'Present' : 'Absent'}
                      </button>
                    </td>
                    <td className="py-2 pr-3">
                      {p.present &&
                        (onTeam.has(p.id) ? (
                          <span className="font-black text-[var(--page-success)]">
                            <span aria-hidden="true">{'\u2713'}</span>
                            <span className="sr-only">On a team</span>
                          </span>
                        ) : (
                          <span>
                            <span aria-hidden="true" className="inline-block size-4 rounded border border-[var(--page-ink-muted)] align-middle" />
                            <span className="sr-only">Not on a team yet</span>
                          </span>
                        ))}
                    </td>
                    <td className="py-2 text-right">
                      <button
                        type="button"
                        className="cursor-pointer text-sm font-semibold text-[var(--page-danger)] underline disabled:cursor-default disabled:opacity-50"
                        disabled={busy || state.locked}
                        onClick={() => act({ type: 'removePerson', id: p.id })}
                      >
                        Remove<span className="sr-only"> {p.name}</span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <details className="mt-4">
          <summary className="cursor-pointer font-semibold">Import a full list</summary>
          <p id="roster-help" className="mt-2 text-sm text-[var(--page-ink-muted)]">
            One person per line: name, email, MPworks (yes/no), present (yes/no). Importing replaces the roster and clears teams.
          </p>
          <label htmlFor="roster-text" className="sr-only">
            Paste roster rows
          </label>
          <textarea
            id="roster-text"
            aria-describedby="roster-help"
            rows={6}
            value={rosterText}
            onChange={(e) => setRosterText(e.target.value)}
            className={`${input} mt-2 font-mono text-sm`}
          />
          <div className="mt-2">
            <button
              type="button"
              className={primary}
              disabled={busy || state.locked || !rosterText.trim()}
              onClick={async () => {
                if (await act({ type: 'importRoster', text: rosterText }, 'Roster imported.')) setRosterText('')
              }}
            >
              Import roster
            </button>
          </div>
        </details>
        {state.locked && <p className="mt-3 text-sm text-[var(--page-ink-muted)]">Unlock the teams to change the roster.</p>}
      </Panel>

      <Panel
        title="Results"
        aside={
          <button type="button" className={button} disabled={busy} onClick={() => act({ type: 'saveResults', results }, 'Results saved.')}>
            Save results
          </button>
        }
      >
        <p className="mb-3 text-[var(--page-ink-muted)]">Published links appear in the participant space. Review each one before saving.</p>
        <ul className="flex flex-col gap-3">
          {results.map((r, i) => (
            <li key={r.id} className="grid items-end gap-2 sm:grid-cols-[1fr_2fr_auto]">
              <label className="flex flex-col text-sm">
                Title {i + 1}
                <input className={input} value={r.title} onChange={(e) => setResults(results.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)))} />
              </label>
              <label className="flex flex-col text-sm">
                Link {i + 1}
                <input
                  className={input}
                  type="url"
                  value={r.url}
                  onChange={(e) => setResults(results.map((x, j) => (j === i ? { ...x, url: e.target.value } : x)))}
                />
              </label>
              <button type="button" className={button} onClick={() => setResults(results.filter((_, j) => j !== i))}>
                Remove<span className="sr-only"> link {i + 1}</span>
              </button>
            </li>
          ))}
        </ul>
        <button type="button" className={`${button} mt-3`} onClick={() => setResults([...results, { id: `result-${Date.now()}`, title: '', url: '' }])}>
          Add link
        </button>
      </Panel>
    </div>
  )
}

const TABS = [
  { to: '/admin', label: 'Roster', end: true },
  { to: '/admin/teams', label: 'Teams' },
]

function AdminShell({ email }: { email: string }) {
  const admin = useAdminEvent()
  const { state, message } = admin

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black tracking-tight">Event administration</h1>
          {state && (
            <p className="mt-1 text-[var(--page-ink-muted)]">
              Signed in as {email}, administrator. Teams {state.locked ? 'locked' : 'unlocked'}, {state.revealedCount} of{' '}
              {state.teams.length} revealed.
            </p>
          )}
        </div>
        <nav aria-label="Administration">
          <ul className="flex flex-wrap gap-1 rounded-lg border border-[var(--page-rule)] p-1">
            {TABS.map((tab) => (
              <li key={tab.to}>
                <NavLink
                  to={tab.to}
                  end={tab.end}
                  className={({ isActive }) =>
                    `block rounded-md px-3.5 py-2 text-sm font-bold ${
                      isActive ? 'bg-[var(--color-royal)] text-white' : 'text-[var(--page-ink-muted)] hover:text-[var(--page-ink)]'
                    }`
                  }
                >
                  {tab.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      </div>

      <div aria-live="polite">
        {message && (
          <p
            role={message.kind === 'error' ? 'alert' : 'status'}
            className={`rounded-md border p-3 font-semibold ${
              message.kind === 'error'
                ? 'border-[var(--page-danger)] text-[var(--page-danger)]'
                : 'border-[var(--page-success)] text-[var(--page-success)]'
            }`}
          >
            {message.text}
          </p>
        )}
      </div>

      {!state ? (
        <p>Loading the event.</p>
      ) : (
        <Routes>
          <Route index element={<Console admin={admin} />} />
          <Route path="teams" element={<TeamStage admin={admin} />} />
        </Routes>
      )}
    </div>
  )
}

export default function AdminPage() {
  const { state, signedIn } = useSession()

  return (
    <div className="page-shell py-10">
      {state.status === 'checking' && <p>Checking your sign-in.</p>}
      {state.status === 'signed-out' && (
        <>
          {state.error && <p className="mb-4 text-[var(--page-danger)]">{state.error}</p>}
          <SignInForm role="admin" onSignedIn={signedIn} />
        </>
      )}
      {state.status === 'signed-in' && state.session.role !== 'admin' && (
        <div className="flex flex-col gap-4">
          <h1 className="text-3xl font-black tracking-tight">Event administration</h1>
          <p>This area is for the people running the event. Sign out from the header and sign in with the administrator address.</p>
        </div>
      )}
      {state.status === 'signed-in' && state.session.role === 'admin' && <AdminShell email={state.session.email} />}
    </div>
  )
}
